import React, { useState, useEffect } from 'react';
import { Lock, Check, Star } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

const EMOJIS_5 = ['😠', '😕', '😐', '🙂', '😄'];
const EMOJIS_10 = ['😠', '😕', '😫', '😐', '🙂', '😊', '😄', '😁', '🤩', '💯'];

const RatingForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const max = Math.min(Math.max(2, Number(content.settings?.maxValue) || 5), 10);
    const initial = userAnswer?.rating != null ? Math.min(Math.max(0, Number(userAnswer.rating) - 1), max - 1) : null;
    const [selected, setSelected] = useState(initial);

    useEffect(() => {
        if (userAnswer?.rating != null) {
            const r = Math.min(Math.max(0, Number(userAnswer.rating) - 1), max - 1);
            setSelected(r);
        } else if (userAnswer == null) setSelected(null);
    }, [userAnswer, max]);

    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && selected !== null && selected + 1 === Number(userAnswer?.rating);
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Submit')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || selected === null || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    const isEmoji = content.settings?.ratingType === 'emojis';
    const symbols = isEmoji
        ? (max <= 5 ? EMOJIS_5 : EMOJIS_10.slice(0, max))
        : null;

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Rate this session'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}
            {content.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
            )}
            {content.settings?.lowLabel && (
                <span className={styles.hint}>{content.settings.lowLabel}</span>
            )}
            <div className={styles.stars}>
                {Array.from({ length: max }, (_, i) => {
                    const isActive = selected !== null && i <= selected;
                    return (
                        <span
                            key={i}
                            className={`${styles.star} ${isActive ? styles.active : ''}`}
                            onClick={() => !optionsDisabled && setSelected(i)}
                            onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && !optionsDisabled) {
                                    e.preventDefault();
                                    setSelected(i);
                                }
                            }}
                            style={{
                                cursor: optionsDisabled ? 'default' : 'pointer',
                                color: isEmoji ? undefined : (isActive ? 'var(--primary-600)' : 'var(--text-muted)'),
                            }}
                            role="button"
                            tabIndex={optionsDisabled ? -1 : 0}
                            aria-label={`Rate ${i + 1} out of ${max}`}
                            aria-pressed={isActive}
                        >
                            {isEmoji ? symbols[i] : (
                                <Star size={28} fill={isActive ? 'currentColor' : 'none'} aria-hidden />
                            )}
                        </span>
                    );
                })}
            </div>
            {content.settings?.highLabel && (
                <span className={styles.hint} style={{ textAlign: 'right' }}>{content.settings.highLabel}</span>
            )}
            <button
                className={styles.submitBtn}
                onClick={() => !optionsDisabled && onSubmit({ rating: selected + 1 })}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default RatingForm;
